import { cn } from '@utima/ui';

export type RowHighlight = 'none' | 'solid' | 'soft';

export type StatSummaryRowProps = {
  title: string;
  description?: string;
  count: number;
  highlight?: RowHighlight;
  isError?: boolean;
  isTotal?: boolean;
  isInactive?: boolean;
  onClick?: () => void;
};

/**
 * Presentational row used inside an expanded {@link StatsSummaryShell}.
 * Shared verbatim between the KB and Products status bars — see
 * `docs/implementation/2026-07-18-product-table-ux-and-feed-settings-modal/plan.md` (D1).
 */
export function StatSummaryRow({
  title,
  description,
  count,
  highlight = 'none',
  isError = false,
  isTotal = false,
  isInactive = false,
  onClick,
}: StatSummaryRowProps) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-2 rounded-radius px-1 py-0.5',
        onClick && !isInactive && 'cursor-pointer hover:bg-muted/50',
        highlight === 'solid' && 'bg-accent ring-1 ring-primary',
        highlight === 'soft' && 'bg-muted/30',
        isTotal && 'mb-0.5 border-b border-border pb-1',
        isInactive && 'opacity-50',
      )}
      onClick={isInactive ? undefined : onClick}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick && !isInactive ? 'button' : undefined}
      tabIndex={onClick && !isInactive ? 0 : undefined}
    >
      <div className='min-w-0 flex-1 leading-tight'>
        <span
          className={cn(
            'text-xs font-medium',
            isTotal && 'text-sm',
            isInactive && 'text-muted-fg',
          )}
        >
          {title}
        </span>
        {description && (
          <p
            className={cn(
              'text-[10px] leading-tight text-muted-fg',
              isInactive && 'text-muted-fg/80',
            )}
          >
            {description}
          </p>
        )}
      </div>
      <strong
        className={cn(
          'shrink-0 text-xs tabular-nums',
          isTotal && 'text-sm font-semibold',
          isInactive && 'font-normal text-muted-fg',
          isError && count > 0 && !isInactive && 'text-danger',
        )}
      >
        {count}
      </strong>
    </div>
  );
}
